// registration-wizard/CourseDetailsStep.tsx
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Center, Loader, Stack, Text } from '@mantine/core';
import {
  useCourseCustomFieldService,
  useCustomFieldTypeService,
  CustomFieldType,
} from '@actschurch/ccms-sdk';
import { DynamicForm, DynamicFormHandle } from '@/components/advanced/FormBuilder';
import { toBuilderField } from '@/components/advanced/FormBuilder/adapters/createCustomFieldAdapter';

export interface CourseDetailsStepProps {
  courseID: number | null;
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onDefinitionsLoaded?: (definitions: any[]) => void;
}

export const CourseDetailsStep = forwardRef<DynamicFormHandle, CourseDetailsStepProps>(
  function CourseDetailsStep({ courseID, values, onChange, onDefinitionsLoaded }, ref) {
    const courseCustomField = useCourseCustomFieldService();
    const customFieldTypeService = useCustomFieldTypeService();

    const [fieldTypes, setFieldTypes] = useState<CustomFieldType[] | null>(null);
    const [definitions, setDefinitions] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!courseID) {
        setDefinitions(null);
        onDefinitionsLoaded?.([]);
        return;
      }
      let cancelled = false;
      setLoading(true);
      setError(null);
      Promise.all([customFieldTypeService.list(), courseCustomField.byCourse(courseID)])
        .then(([types, defsResponse]) => {
          if (cancelled) return;
          setFieldTypes(types);
          const defs = Array.isArray(defsResponse) ? defsResponse : (defsResponse as any)?.data ?? [];
          setDefinitions(defs);
          onDefinitionsLoaded?.(defs);
        })
        .catch((e) => !cancelled && setError(e?.message ?? 'Failed to load course questions.'))
        .finally(() => !cancelled && setLoading(false));
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseID, courseCustomField, customFieldTypeService]);

    const typesById = useMemo(
      () => new Map((fieldTypes ?? []).map((t) => [t.typeID, t])),
      [fieldTypes]
    );

    const builderFields = useMemo(() => {
      if (!definitions) return [];
      return definitions
        .filter((d) => d.isActive)
        .sort((a: any, b: any) => a.orderNo - b.orderNo)
        .map((d) => toBuilderField(d, typesById));
    }, [definitions, typesById]);

    if (!courseID) {
      return <Text size="sm" c="dimmed">Select a course first to see its additional questions.</Text>;
    }

    if (loading || !fieldTypes || !definitions) {
      return (
        <Center py="xl">
          <Loader />
        </Center>
      );
    }

    if (error) {
      return <Text size="sm" c="red">{error}</Text>;
    }

    if (builderFields.length === 0) {
      return <Text size="sm" c="dimmed">This course has no additional questions.</Text>;
    }

    return (
      <Stack gap="md">
        <DynamicForm ref={ref} fields={builderFields} values={values} onChange={onChange} hideSubmit />
      </Stack>
    );
  }
);

export default CourseDetailsStep;